import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Turnstile } from '@marsidev/react-turnstile';
import { AlertTriangle } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import SectionGroup from '../components/SectionGroup';
import { contactApi, type ContactFormPayload } from '../lib/api/contact';
import { formatContextForMessage, type BetaFeedbackContext } from '../utils/betaFeedbackContext';

type FormState = 'form' | 'success' | 'error';

interface ContactFormData {
  name: string;
  email: string;
  reason: string;
  message: string;
  clubOrganization: string;
  howDidYouHear: string;
  consent: boolean;
  // Honeypot field
  website: string;
  // Turnstile token
  turnstileToken: string | null;
}

export default function ContactPage() {
  const location = useLocation();
  const state = location.state as { source?: string; context?: BetaFeedbackContext };
  const isBetaFeedback = state?.source === 'beta_widget';
  const betaContext = state?.context;
  
  const [formState, setFormState] = useState<FormState>('form');
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    reason: '',
    message: '',
    clubOrganization: '',
    howDidYouHear: '',
    consent: false,
    website: '', // Honeypot
    turnstileToken: null
  });
  const startedAt = useRef<number>(0);
  
  // Initialize startedAt when component mounts
  useEffect(() => {
    startedAt.current = Date.now();
  }, []);

  // Pre-populate form if from beta widget
  useEffect(() => {
    if (isBetaFeedback && betaContext) {
      setFormData(prev => ({
        ...prev,
        email: betaContext.email,
        reason: 'general', // Default to general for beta feedback
        message: `${prev.message}${formatContextForMessage(betaContext)}`
      }));
    }
  }, [isBetaFeedback, betaContext]);

  const mutation = useMutation({
    mutationFn: contactApi.submitMessage,
    onSuccess: () => {
      setFormState('success');
    },
    onError: (error) => {
      console.error('Contact form submission failed:', error);
      setFormState('error');
    }
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim() || !formData.consent) {
      return;
    }

    if (formData.message.length < 20) {
      return;
    }

    // Check honeypot (keep local, don't send to backend)
    if (formData.website) {
      console.log('Bot detected via honeypot');
      return;
    }

    // Transform payload to match backend schema (ContactFormSchema)
    const payload: ContactFormPayload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.reason || undefined,  // Map reason → subject
      message: formData.message.trim(),
      ...(formData.turnstileToken && { turnstile_token: formData.turnstileToken })  // Snake case
    };
    // Note: clubOrganization, howDidYouHear, consent, website, startedAt NOT sent (backend doesn't validate them)

    mutation.mutate(payload);
  };

  const resetForm = () => {
    setFormState('form');
    setFormData({
      name: '',
      email: '',
      reason: '',
      message: '',
      clubOrganization: '',
      howDidYouHear: '',
      consent: false,
      website: '',
      turnstileToken: null
    });
  };

  if (formState === 'success') {
    return (
      <PageLayout maxWidth="narrow">
        <div className="text-center">
          <div className="bg-oro-surface border border-oro-border rounded-xl p-8 shadow-card">
            <div className="text-accent text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-semibold text-text-primary mb-3">Message sent!</h2>
            <p className="text-text-secondary mb-4">
              Thanks for reaching out. Whether your group moves on foot, wheels, trails, courts, or wherever else, we're glad you're here.
            </p>
            <p className="text-text-muted mb-6">
              We'll reply as soon as we can — and your email won't be sold or added to any marketing lists.
            </p>
            <button 
              onClick={resetForm}
              className="bg-transparent border border-oro-border text-accent hover:bg-accent/10 hover:border-accent transition-colors px-6 py-2 rounded-lg"
            >
              Send another message
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="narrow">
      <PageHeader 
        title="Say hi to ORO MOVE™"
        subtitle="Questions, bug reports, partnership ideas, or 'my group's meetups went feral again'? Let us know. A real person reads these — usually with coffee in hand."
      />

      <SectionGroup>
        <Section>
          <div className="text-sm text-text-muted mb-6">
            <strong>Average response time:</strong> under a long run.
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-6 bg-oro-surface border border-oro-border rounded-xl p-6 shadow-card">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-text-primary mb-2">
                {isBetaFeedback ? 'Beta Feedback Form' : 'Contact Form'}
              </h3>
              <p className="text-text-muted text-sm">
                {isBetaFeedback 
                  ? 'Thanks for helping us improve ORO! Your feedback is valuable.'
                  : 'We can\'t promise miracles, but we can promise a thoughtful reply.'
                }
              </p>
            </div>

            {isBetaFeedback && (
              <div className="bg-oro-gold/10 border border-oro-gold/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-oro-gold text-sm">✨</span>
                  <h4 className="text-sm font-medium text-text-primary">Beta User Feedback</h4>
                </div>
                <p className="text-text-secondary text-sm">
                  Your form has been pre-filled with context to help us understand your feedback better.
                </p>
              </div>
            )}

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-2">
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                maxLength={80}
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                placeholder="Your name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                placeholder="your.email@example.com"
              />
            </div>

            {/* Reason */}
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-text-primary mb-2">
                What's this about?
              </label>
              <select
                id="reason"
                name="reason"
                required
                value={formData.reason}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
              >
                <option value="">Select a reason...</option>
                <option value="general">General question</option>
                <option value="bug">Bug report</option>
                <option value="partnership">Club / team / group partnership</option>
                <option value="press">Press / media</option>
                <option value="other">Something else</option>
              </select>
            </div>

            {/* Club/Organization (Optional) */}
            <div>
              <label htmlFor="clubOrganization" className="block text-sm font-medium text-text-primary mb-2">
                Club / organization <span className="text-text-muted font-normal">(optional)</span>
              </label>
              <input
                id="clubOrganization"
                name="clubOrganization"
                type="text"
                value={formData.clubOrganization}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                placeholder="Your club, team, or organization"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
                Tell us what's going on
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                minLength={20}
                maxLength={2000}
                value={formData.message}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors resize-vertical"
                placeholder="Your group type, the issue, or your idea..."
              />
              <div className="mt-1 text-xs text-text-muted">
                {formData.message.length < 20 && formData.message.length > 0 && (
                  <span className="text-oro-warning">Mind adding a few more details? ({20 - formData.message.length} characters needed)</span>
                )}
                {formData.message.length >= 20 && (
                  <span className="text-accent">Looking good! ({formData.message.length}/2000 characters)</span>
                )}
              </div>
            </div>

            {/* How did you hear about us (Optional) */}
            <div>
              <label htmlFor="howDidYouHear" className="block text-sm font-medium text-text-primary mb-2">
                How did you hear about ORO MOVE<sup>™</sup>? <span className="text-text-muted font-normal">(optional)</span>
              </label>
              <input
                id="howDidYouHear"
                name="howDidYouHear"
                type="text"
                value={formData.howDidYouHear}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                placeholder="Word of mouth, (anti) social media, search, etc."
              />
            </div>

            {/* Honeypot field (hidden) */}
            <input
              name="website"
              type="text"
              value={formData.website}
              onChange={handleInputChange}
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Cloudflare Turnstile Bot Protection */}
            {turnstileSiteKey && (
              <div className="space-y-3">
                <div className="text-sm font-medium text-text-primary mb-2">
                  Security verification
                </div>
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => {
                      setFormData(prev => ({ ...prev, turnstileToken: token }));
                      setTurnstileError(false);
                    }}
                    onError={() => {
                      setFormData(prev => ({ ...prev, turnstileToken: null }));
                      setTurnstileError(true);
                    }}
                    onExpire={() => {
                      setFormData(prev => ({ ...prev, turnstileToken: null }));
                    }}
                    options={{
                      theme: 'dark',
                      size: 'normal',
                      retry: 'auto',
                      appearance: 'always',
                    }}
                  />
                </div>
                {turnstileError && (
                  <div className="rounded-md border border-oro-border bg-oro-surface-alt p-3">
                    <p className="text-xs text-text-secondary text-center">
                      Security verification failed. Please refresh the page and try again.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Consent Checkbox */}
            <div className="flex items-start space-x-3">
              <input
                id="consent"
                name="consent"
                type="checkbox"
                required
                checked={formData.consent}
                onChange={handleInputChange}
                className="mt-1 h-4 w-4 text-accent border-oro-border rounded focus:ring-accent focus:ring-2"
              />
              <label htmlFor="consent" className="text-sm text-text-secondary">
                It's okay to email me back about this, and I've read the{' '}
                <Link to="/privacy" className="text-accent hover:underline">
                  Privacy Policy
                </Link>
                .
              </label>
            </div>

            {/* Email Promise */}
            <div className="bg-oro-surface-alt rounded-lg p-4 border border-oro-border">
              <p className="text-sm text-text-muted">
                <strong className="text-text-secondary">No spam.</strong> We'll never sell your email.
              </p>
            </div>

            {/* Error message displayed near submit button for better UX */}
            {formState === 'error' && (
              <div className="bg-oro-error-soft border border-oro-error-border rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-oro-error shrink-0" />
                  <p className="text-oro-error text-sm">
                    Something went wrong on our side. Try again in a minute.
                  </p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={mutation.isPending || !formData.consent || formData.message.length < 20}
              className="w-full bg-accent hover:bg-accent-light active:bg-accent-dark disabled:bg-accent/50 disabled:cursor-not-allowed text-black font-medium py-3 px-6 rounded-lg transition-colors"
            >
              {mutation.isPending ? 'Sending...' : 'Send message'}
            </button>
          </form>
        </Section>
      </SectionGroup>
    </PageLayout>
  );
}